import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCwIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { ScanProgress } from '@/hooks/use-scan-progress';
import { gcpServiceAccountQueries } from '@/lib/queries/gcpServiceAccount';
import { awsCredentialsQueries } from '@/lib/queries/integrations/cloud/aws/credentials';
import { awsRoleQueries } from '@/lib/queries/integrations/cloud/aws/roles';
import { k8sClustersQueries } from '@/lib/queries/integrations/containers/k8sClusters';
import { githubConfigQueries } from '@/lib/queries/integrations/scm/github';
import { terraformBackendBucketsQueries } from '@/lib/queries/integrations/terraformBackend/buckets';
import type { TranslationKeys } from '@/lib/translations/types';
import type { FileRouteTypes } from '@/routeTree.gen';

import { IntegrationGrid } from './IntegrationGrid';
import { IntegrationTabs } from './IntegrationTabs';

export type IntegrationsData = {
  categoryTitle: TranslationKeys;
  items: {
    title: TranslationKeys;
    description?: TranslationKeys;
    logoUrl: string;
    connected: boolean;
    available: boolean;
    to?: FileRouteTypes['to'];
    onClick?: () => void;
    pending?: boolean;
    warnings?: TranslationKeys[];
    donePercentage?: number;
    scanProgress?: ScanProgress;
    isSyncing?: boolean;
  }[];
}[];

interface Props {
  data: IntegrationsData;
  defaultProjectId: string;
}

export const Integrations = ({
  data,
  defaultProjectId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<
    TranslationKeys | 'all'
  >('all');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshIntegrations = async () => {
    if (isRefreshing) {
      return;
    }

    console.log('Starting refresh - setting isRefreshing to true');
    setIsRefreshing(true);
    try {
      // TODO - Refactor this, this kind of listing isnt scalable
      await Promise.all([
        queryClient.invalidateQueries(
          terraformBackendBucketsQueries.list(defaultProjectId),
        ),
        queryClient.invalidateQueries(
          awsCredentialsQueries.list(defaultProjectId),
        ),
        queryClient.invalidateQueries(awsRoleQueries.list(defaultProjectId)),
        queryClient.invalidateQueries(
          gcpServiceAccountQueries.list(defaultProjectId),
        ),
        queryClient.invalidateQueries(
          githubConfigQueries.list(defaultProjectId),
        ),
        queryClient.invalidateQueries(
          k8sClustersQueries.list(defaultProjectId),
        ),
        // Add minimum delay to ensure loading state is visible
        new Promise(resolve => setTimeout(resolve, 800)),
      ]);
    } catch {
      toast({
        title: 'integrations.refresh.error',
        variant: 'destructive',
      });
    } finally {
      console.log('Refresh complete - setting isRefreshing to false');
      setIsRefreshing(false);
    }
  };

  // Separate connected and available integrations
  const connectedIntegrations = data.flatMap(({ items }) =>
    items.filter(({ connected }) => connected),
  );

  // Get all non-connected integrations (both available and unavailable - unavailable show as "coming soon")
  const availableIntegrations = data.flatMap(({ items }) =>
    items.filter(({ connected }) => !connected),
  );

  // Create categories with counts for tabs (include both available and unavailable)
  const categories = data
    .map(({ categoryTitle, items }) => ({
      categoryTitle,
      count: items.filter(({ connected }) => !connected).length,
    }))
    .filter(({ count }) => count > 0);

  // Filter integrations based on selected category (include both available and unavailable)
  const filteredIntegrations =
    selectedCategory === 'all'
      ? availableIntegrations
      : (data
          .find(({ categoryTitle }) => categoryTitle === selectedCategory)
          ?.items.filter(({ connected }) => !connected) ?? []);

  return (
    <>
      <div className="flex flex-row items-baseline">
        <h1 className="text-3xl">
          <FormattedMessage id="integrations.title" />
        </h1>
        <Button
          variant="ghost"
          className="ml-2 h-8 w-8 p-0"
          onClick={refreshIntegrations}
          disabled={isRefreshing}
        >
          <RefreshCwIcon
            className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
        </Button>
        {/* Debug info */}
        <span className="ml-2 text-xs text-gray-500">
          {isRefreshing ? 'Refreshing...' : 'Ready'}
        </span>
      </div>

      {/* Connected Integrations Section */}
      {connectedIntegrations.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">
            <FormattedMessage id="integrations.connected.title" />
          </h3>
          <IntegrationGrid integrations={connectedIntegrations} />
        </div>
      )}

      {/* Available Integrations Section with Tabs */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-4">
          <FormattedMessage id="integrations.available.title" />
        </h3>

        {/* Tab Navigation */}
        <div className="mb-6">
          <IntegrationTabs
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </div>

        {/* Integration Grid */}
        <IntegrationGrid integrations={filteredIntegrations} />
      </div>
    </>
  );
};
